import { Grid } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import { FeedClassKey } from "assets/jss/common-styles";
import { ReactLightBoxGallery } from "components/LightBox/react-lightbox-gallery";
import { CommonPlayerApi } from "components/VideoPlayer";
import { FeedType, PublicationActionType, PublicationType } from "models/ApiEnums";
import { FeedItemDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import AttachMetadataBox from "views/Publication/Components/AttachMetadataBox";
import { CourseActions } from "views/Publication/Components/CourseModels";
import RenderImage from "views/Publication/Components/RenderImage";
import { RenderVideoEventsProps } from "views/Publication/Components/RenderVideo";
import BodyTextExpand from "views/Timeline/Components/CardComp/BodyTextExpand";
import StatusLine from "views/Timeline/Components/CardComp/StatusLine";
import { CardBodyData } from "views/Timeline/Components/CardLib/CardBodyData";
import Publisher from "./Publisher";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    lighBoxIndex: number;
    lighBoxOpen: boolean;
}

interface IProps {
    /** Feed Item */
    data: FeedItemDto;

    /** Está em página secundária? */
    fullView: boolean;

    /** Texto deve ficar sempre expandido? */
    alwaysExpanded?: boolean;

    /** Exibir Botão de Expansão de Conteúdo mesmo que o conteúdo esteja vazio? */
    showExpandButtonIfContentEmpty?: boolean;

    /** Exibir Publicador */
    showPublisher?: boolean;

    /** Tipo de Feed */
    feedType: FeedType;

    /** Evento ocorre quando o botão de expansão/contração é pressionado. */
    onTextExpandedChange?: (expanded: boolean) => void;

    onCustomAction?: (action: any) => Promise<boolean>;
}

type Props = IProps & StyledComponentProps<FeedClassKey> & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

class BodyDefault extends React.PureComponent<Props, IState> {

    private data: CardBodyData;

    constructor(props: Props) {
        super(props);

        this.state = { lighBoxOpen: false, lighBoxIndex: 0 };
    }

    static defaultProps = {
        alwaysExpanded: false,
        showButtonIfContentEmpty: false,
        showPublisher: true
    };

    ///
    private handleLighBoxClose = () => {
        this.setState({ lighBoxOpen: false, lighBoxIndex: 0 });
    };

    private autoCompleteCourse(): boolean {
        const { feedType, data: { interaction } } = this.props;

        return feedType == FeedType.CourseViewer && interaction.courseItem.completedOn == null && interaction.courseEnroll.completedOn == null && !interaction.courseEnroll.expired;
    };

    ///
    private renderQuestionFullView() {
        const { classes } = this.props;
        const rightPicture = this.data.getPictureUrl();
        const showAttach = this.data.hasAttach() && (!this.data.containsSingleImage() || this.data.emptyContent() || this.data.isLinkOnly());
        const showPicture = !showAttach && this.data.containsSingleImage();

        return (
            <div className={classes.cardBody}>

                <Grid container>
                    <Grid item xs>
                        <Publisher {...this.props} ignoreBestAnswer />

                        <StatusLine {...this.props} />

                        {
                            this.data.isLinkOnly()
                                ? null
                                : <BodyTextExpand
                                    classes={classes}
                                    text={this.data.content}
                                    expanded={this.props.alwaysExpanded || this.data.textExpanded}
                                    onTextExpandedChange={this.props.alwaysExpanded ? null : this.props.onTextExpandedChange}
                                />
                        }
                    </Grid>

                    {
                        showPicture &&
                        <Grid item>
                            <RenderImage
                                image={rightPicture.mainFile}
                                thumbnail={rightPicture.thumbnail ? rightPicture.thumbnail : null}
                                classes={this.props.classes}
                                alt="Thumbnail"
                            />
                        </Grid>
                    }
                </Grid>

                {
                    showAttach &&
                    <div>
                        <AttachMetadataBox
                            fileOrLink={this.data.metadata || this.data.nonEmbedAttachs}
                        />
                    </div>
                }

            </div>
        );

    }

    ///
    private renderBody() {
        const {
            classes,
            data: { publication, publicationType, permissions, bestAnswer, action },
            fullView,
            showExpandButtonIfContentEmpty,
            showPublisher,
            feedType,
            onCustomAction
        } = this.props;

        const expanded = this.data.textExpanded == null
            ? null  // Não haverá expansão pois "body == summary"
            : (this.props.alwaysExpanded || this.data.textExpanded);

        // As perguntas podem ter um CORPO (descrição), mas o que prevalece é o Corpo da Resposta, salvo se está aprovando a publicação.
        let showContent = showExpandButtonIfContentEmpty == true || this.data.summary != null || this.data.content != null || this.data.textExpanded === false;

        // Questões
        if (publicationType == PublicationType.Question) {
            // Está aprovando?
            if (!action || action.actionType != PublicationActionType.ApproveWorkflow) {
                // Não está aprovando.

                // Possui uma resposta?
                if (bestAnswer == null) {
                    // Não possui resposta, então não mostra corpo da Questão (exibir corpo somente na página secundária)
                    showContent = false;
                }
                //    else {
                //        // Contém uma resposta, então o CORPO exibido é o da resposta.
                //        showContent = showExpandButtonIfContentEmpty == true || bestAnswer.body.summary != null || bestAnswer.body.fullContent != null || bestAnswer.body.fullContentLoaded === false;
                //    }
            }
        }

        const rightPicture = this.data.getPictureUrl();
        const contentLightboxData = this.data.getContentLightBoxData();
        const hasContentLightbox = contentLightboxData != null;

        const showAttach =
            this.data.hasAttach()
            && (
                expanded === null || expanded === true
                ||
                (expanded === false && this.data.containsSingleImage() && this.data.emptyContent())
                ||
                this.data.isLinkOnly()
            )
            && !publication.blocked;

        const showRightPicture =
            !showAttach
            && rightPicture
            && expanded !== true
            && !this.data.emptyContent();

        const handleImageClick =
            ((publicationType == PublicationType.Post || publicationType == PublicationType.Question || publicationType == PublicationType.Answer) && hasContentLightbox)
                ? (e: React.MouseEvent<HTMLImageElement>) => {
                    var index = contentLightboxData.images.findIndex(p => p.url.toLowerCase() == e.currentTarget.currentSrc.replace(window.location.origin, "").toLowerCase());
                    this.setState({ lighBoxOpen: true, lighBoxIndex: index == -1 ? 0 : index });
                }
                : null;

        const customActions: RenderVideoEventsProps = feedType == FeedType.CourseViewer
            ? {
                onSeeking: (player: CommonPlayerApi) => onCustomAction({ player: { actionName: "seeking", player } } as CourseActions),
                onSeeked: (player: CommonPlayerApi) => onCustomAction({ player: { actionName: "seeked", player } } as CourseActions),
                onEnded: (player: CommonPlayerApi) => onCustomAction({ player: { actionName: "ended", player } } as CourseActions),
                onProgress: (player: CommonPlayerApi) => onCustomAction({ player: { actionName: "progress", player } } as CourseActions),
                onReady: (player: CommonPlayerApi) => onCustomAction({ player: { actionName: "ready", player } } as CourseActions),
            }
            : {};

        return (

            <div className={classes.cardBody}>

                <Grid container wrap="nowrap" spacing={1}>

                    <Grid item xs>
                        {
                            showPublisher &&
                            <Publisher {...this.props} ignoreBestAnswer={fullView} />
                        }

                        <StatusLine {...this.props} />

                        {
                            showContent && permissions.canViewBody && !this.data.isLinkOnly() &&
                            <BodyTextExpand
                                classes={classes}
                                text={this.data.content}
                                expanded={expanded}
                                onTextExpandedChange={this.props.alwaysExpanded ? null : this.props.onTextExpandedChange}
                                onImageClick={handleImageClick}
                            />
                        }
                    </Grid>

                    {
                        showRightPicture &&
                        <Grid item>
                            <div className={this.props.classes.contentPictureArea}>
                                <RenderImage
                                    image={rightPicture.mainFile}
                                    thumbnail={rightPicture.thumbnail ? rightPicture.thumbnail : null}
                                    classes={this.props.classes}
                                    alt="Thumbnail"
                                />
                            </div>
                        </Grid>
                    }
                </Grid>

                {
                    showAttach &&
                    <AttachMetadataBox
                        fileOrLink={this.data.metadata || this.data.nonEmbedAttachs}
                        autoplay={feedType != FeedType.CourseViewer && fullView}
                        autoCompleteCourse={this.autoCompleteCourse()}
                        onCustomAction={onCustomAction}
                        {...customActions}
                    />
                }

                {
                    hasContentLightbox &&
                    <ReactLightBoxGallery
                        index={this.state.lighBoxIndex}
                        images={contentLightboxData.images}
                        thumbnails={contentLightboxData.thumbnails}
                        isOpen={this.state.lighBoxOpen}
                        onClose={this.handleLighBoxClose}
                    />
                }
            </div>
        );
    }

    render() {
        const { fullView, data, alwaysExpanded } = this.props;

        this.data = new CardBodyData(data, fullView, alwaysExpanded);

        return data.publicationType == PublicationType.Question && fullView
            ? this.renderQuestionFullView()
            : this.renderBody();
    }
}

export default withTranslation()(BodyDefault);